import * as Utils from './utils';

export const TransitionGuardType = {
  ensureBookingWindowClosed: 'ensure_booking_window_closed',
  ensureBookingWindowOpen:   'ensure_booking_window_open',
  ensurePaymentsPaid:        'ensure_payments_paid',
  ensureBookingPaid:         'ensure_booking_paid',
  formFieldGuard:            'field_guard',
} as const;
type ValueOf<T> = T[keyof T];
export type TransitionGuardType = ValueOf<typeof TransitionGuardType>;

export type TransitionGuardData = {[key: string]: string|number|undefined};

export interface TransitionGuardOption {
    id: string;
    name: string;
}

export class TransitionGuard {
    private static ID_PREFIX = 'wtg-';

    public static readonly AvailableGuards: TransitionGuard[] = [
        new TransitionGuard(undefined,
                            $localize `Booking item field value`,
                            TransitionGuardType.formFieldGuard,
                            [
                              {id: 'field', name: $localize `Field name`},
                              {id: 'op', name: $localize `Operator`},
                              {id: 'value', name: $localize `Field value`},
                            ]
                           ),
        new TransitionGuard(undefined, $localize `All payment requests complete or cancelled`, TransitionGuardType.ensurePaymentsPaid),
        new TransitionGuard(undefined, $localize `Price of Booking Option paid in full`, TransitionGuardType.ensureBookingPaid),
        new TransitionGuard(undefined, $localize `Booking window is open`, TransitionGuardType.ensureBookingWindowOpen),
        new TransitionGuard(undefined, $localize `Booking window is closed`, TransitionGuardType.ensureBookingWindowClosed),
      ].sort((a, b) => a.name.localeCompare(b.name));

    public readonly id: string;
    public readonly type: TransitionGuardType;
    public readonly name: string;
    public readonly options?: TransitionGuardOption[];
    public readonly data?: TransitionGuardData;

    public get isTemplate(): boolean {
        return !this.data;
    }

    public get hasOptions(): boolean {
        return !!this.options;
    }

    public get description(): string {
        switch (this.type) {
            case TransitionGuardType.ensureBookingWindowClosed:
                return $localize `Allow transition only if booking window is closed.`;
            case TransitionGuardType.ensureBookingWindowOpen:
                return $localize `Allow transition only if booking window is open.`;
            case TransitionGuardType.ensurePaymentsPaid:
                return $localize `Allow transition only if all payments are complete.`;
            case TransitionGuardType.ensureBookingPaid:
                return $localize `Allow transition only if price of Booking Option paid in full.`;
            case TransitionGuardType.formFieldGuard:
                return $localize `Allow transition only when the below fields on the form match the specified settings.`;
        }
    }

    public constructor(id: string|undefined,
                       name: string,
                       type: TransitionGuardType,
                       options?: TransitionGuardOption[],
                       data?: TransitionGuardData) {
        this.id = id ?? `${TransitionGuard.ID_PREFIX}${Utils.randomString()}`;
        this.name = name;
        this.type = type;
        this.options = options;
        this.data = data;
    }

    public evalueToGuard(id?: string, data?: TransitionGuardData): TransitionGuard {
        const guard =
            new TransitionGuard(id, this.name, this.type, this.options, data);
        return guard;
    }
}
